import React, { createContext, useContext, useMemo, useState } from 'react';

import { Alert, Button, Stack, Tooltip, Typography } from '@mui/material';

import ToursPricingApplyDialog from '~/components/LERE/ToursPricingApplyDialog';
import {
  buildAppliedPreview,
  buildApplyRequest,
  getPercentInputValue,
  validateAppliedPercentInput,
} from '~/components/LERE/toursPricingApply';
import { LereToursPricingHistoryRecord, LereToursPricingSuggestion } from '~/components/LERE/types';

import { useApplyTccToursPricingSuggestion } from '~/hooks/apis/lere/useApplyTccToursPricingSuggestion';
import { useGetLereToursPricingSuggestions } from '~/hooks/apis/lere/useGetLereToursPricingSuggestions';
import { useGetToursPricingHistory } from '~/hooks/apis/lere/useGetToursPricingHistory';
import { useAppSelector } from '~/hooks/store';

import { UserType } from '~/types/responses';

import { hasFullAccess, hasReadAccess } from '~/utils/lere';

import { indexPricingSuggestions, matchPricingSuggestion, pricingConflictMessage } from './pricingMatch';
import type { PricingSuggestionIndex } from './pricingMatch';
import type { CommandDeparture, CommandPackage } from './types';

type PricingContext = {
  suggestions: PricingSuggestionIndex;
  history: Array<LereToursPricingHistoryRecord>;
  accept: (row: LereToursPricingSuggestion) => void;
  reject: (row: LereToursPricingSuggestion) => void;
  pending: boolean;
  error: string | null;
};
const Pricing = createContext<PricingContext | null>(null);
export const canReviewPricing = (user: UserType | null) => hasFullAccess(user) || hasReadAccess(user);

/** Mount query hooks only for the exact RM Pricing access gate. */
export default function TccPricingProvider({ children }: { children: React.ReactNode }) {
  const user = useAppSelector((state) => state.currentUser?.user ?? null);
  return canReviewPricing(user) ? <PermittedPricing>{children}</PermittedPricing> : <>{children}</>;
}
function PermittedPricing({ children }: { children: React.ReactNode }) {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
  const suggestions = useGetLereToursPricingSuggestions({ page: 1, limit: 5000 }, { staleTime: 60 * 60 * 1000 });
  const suggestionIndex = useMemo(
    () => indexPricingSuggestions(suggestions.data?.result ?? []),
    [suggestions.data?.result],
  );
  const history = useGetToursPricingHistory(
    {
      page: 1,
      limit: 1000,
      created_at_start: `${today}T00:00:00${sydneyOffset(today)}`,
    },
    { staleTime: 60 * 60 * 1000 },
  );
  const [dialog, setDialog] = useState<LereToursPricingSuggestion | null>(null);
  const [percent, setPercent] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [local, setLocal] = useState<Array<LereToursPricingHistoryRecord>>([]);
  const mutation = useApplyTccToursPricingSuggestion({
    onSuccess: (_result, body) => {
      const source = suggestions.data?.result.find((s) => s.suggestion_id === body.suggestion_id) ?? dialog;
      setLocal((rows) => [
        ...rows.filter((r) => r.suggestion_id !== body.suggestion_id),
        {
          ...body,
          bus_id: source?.bus_id,
          departure_salesforce_bk: source?.departure_salesforce_bk,
          id: body.suggestion_id,
          status: body.status === 'accepted' ? 'applied' : 'rejected',
          created_at: new Date().toISOString(),
          created_by_user_id: null,
          created_by_username: null,
        } as LereToursPricingHistoryRecord,
      ]);
      setDialog(null);
      setError(null);
    },
    onError: (err: Error) => setError(pricingConflictMessage(err.message)),
  });
  const accept = (row: LereToursPricingSuggestion) => {
    setDialog(row);
    setPercent(getPercentInputValue(row.suggested_price_change_percent));
    setNote(row.note ?? '');
    setError(null);
  };
  const reject = (row: LereToursPricingSuggestion) =>
    mutation.mutate(buildApplyRequest(row, 'rejected', undefined, row.note ?? undefined));
  const confirm = () => {
    const validated = validateAppliedPercentInput(percent);
    if (dialog && validated.value != null) {
      mutation.mutate(buildApplyRequest(dialog, 'accepted', buildAppliedPreview(dialog, validated.value), note));
    }
  };
  return (
    <Pricing.Provider
      value={{
        suggestions: suggestionIndex,
        history: [...local, ...(history.data?.result ?? [])],
        accept,
        reject,
        pending: mutation.isPending,
        error:
          error ??
          (suggestions.isError
            ? 'Pricing recommendations are unavailable.'
            : history.isError
              ? 'Today’s pricing actions are unavailable.'
              : null),
      }}
    >
      {children}
      <ToursPricingApplyDialog
        applyDialogRow={dialog}
        closeApplyDialog={() => setDialog(null)}
        appliedPercentInput={percent}
        setAppliedPercentInput={setPercent}
        isApplying={mutation.isPending}
        handleApplyDialogConfirm={confirm}
        note={note}
        setNote={setNote}
      />
      {error && <Alert severity="error">{error}</Alert>}
    </Pricing.Provider>
  );
}
// Use the Sydney offset for midnight on the requested day, including daylight saving.
function sydneyOffset(day: string) {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney',
    timeZoneName: 'longOffset',
  }).formatToParts(new Date(`${day}T00:00:00Z`));
  return parts.find((p) => p.type === 'timeZoneName')?.value.replace('GMT', '') || '+00:00';
}
export function RecommendationPending({ departure }: { departure: CommandDeparture }) {
  const context = useContext(Pricing);
  return context && matchPricingSuggestion(context.suggestions, departure) ? (
    <Typography variant="caption">Rec pending</Typography>
  ) : null;
}
export function TccPricingRecommendation({ departure, pkg }: { departure: CommandDeparture; pkg?: CommandPackage }) {
  const context = useContext(Pricing);
  if (!context) {
    return null;
  }
  const rec = matchPricingSuggestion(context.suggestions, departure, pkg);
  const history = context.history
    .filter(
      (h) =>
        h.departure_date === departure.dep &&
        (h.suggestion_id === rec?.suggestion_id ||
          (pkg && pkg.type !== 'base'
            ? h.bus_id === departure.dep_id && h.offer_salesforce_bk === pkg.package_opp_id
            : h.departure_salesforce_bk === departure.dep_id &&
              (!pkg || h.offer_salesforce_bk === pkg.package_opp_id))),
    )
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))[0];
  if (history) {
    return (
      <Typography variant="caption" color="text.secondary">
        {['applied', 'accepted'].includes(history.status)
          ? `Applied ${history.applied_price_change_percent >= 0 ? '+' : '−'}${Math.abs(history.applied_price_change_percent * 100)}% today. TCC prices update after tomorrow's 6:30am refresh.`
          : 'Rejected today'}
      </Typography>
    );
  }
  if (!rec) {
    return context.error ? (
      <Typography variant="caption" color="error">
        {context.error}
      </Typography>
    ) : null;
  }
  const value = rec.suggested_price_change_percent;
  return (
    <Stack gap={0.5} sx={{ minWidth: 0 }}>
      <Tooltip title={[rec.reason_headline, ...(rec.reason_points ?? [])].filter(Boolean).join('\n')}>
        <Typography variant="caption">
          {value >= 0 ? '+' : '−'}
          {Math.abs(value * 100)}% {value >= 0 ? 'surcharge' : 'discount'} recommended
        </Typography>
      </Tooltip>
      <Stack direction="row" gap={0.5}>
        <Button
          size="small"
          disabled={context.pending}
          onClick={(e) => {
            e.stopPropagation();
            context.accept(rec);
          }}
        >
          Accept
        </Button>
        <Button
          size="small"
          disabled={context.pending}
          onClick={(e) => {
            e.stopPropagation();
            context.reject(rec);
          }}
        >
          Reject
        </Button>
      </Stack>
    </Stack>
  );
}

export function AnyRecommendationPending({ rows }: { rows: Array<CommandDeparture> }) {
  const context = useContext(Pricing);
  return context &&
    rows.some(
      (d) =>
        matchPricingSuggestion(context.suggestions, d) ||
        d.packages?.some((p) => matchPricingSuggestion(context.suggestions, d, p)),
    ) ? (
    <Typography variant="caption">Rec pending</Typography>
  ) : null;
}
